/**
 * GTM DataLayer Tracker for CTM Chat Widget
 * Handles all Google Tag Manager dataLayer event tracking
 * Event names match the custom events dispatched to window (ctm-chat-*)
 */
export class GTMTracker {
  constructor(logger, { enabled = true } = {}) {
    this.logger = logger || console;
    this.enabled = enabled;
    this.initialized = false;
    if (this.enabled) {
      this.initializeDataLayer();
    }
  }

  setEnabled(enabled) {
    this.enabled = !!enabled;
    if (this.enabled && !this.initialized) {
      this.initializeDataLayer();
    }
  }

  /**
   * Initialize dataLayer if not present
   */
  initializeDataLayer() {
    if (this.enabled && typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      this.initialized = true;
    }
  }

  /**
   * Safely push event to dataLayer
   * Event names use underscores for GTM compatibility (ctm_chat_*)
   * @param {string} event - Event name
   * @param {Object} data - Event data
   */
  pushEvent(event, data = {}) {
    if (!this.enabled) {
      return;
    }

    if (!this.initialized) {
      this.initializeDataLayer();
    }

    if (typeof window !== 'undefined' && window.dataLayer) {
      try {
        // Convert dashes to underscores for GTM event names
        const gtmEventName = event.replace(/-/g, '_');
        window.dataLayer.push({
          event: gtmEventName,
          ctmChat: {
            ...data,
            timestamp: new Date().toISOString()
          }
        });
      } catch (error) {
        this.logger.warn('Failed to push event to dataLayer:', error);
      }
    }
  }

  /**
   * Track widget initialization
   * @param {Object} config - Widget configuration
   */
  trackWidgetInit(config = {}) {
    this.pushEvent('ctm-chat-init', {
      action: 'widget_initialized',
      widgetToken: config.token || 'unknown',
      sessionId: config.sessionId || null
    });
  }

  /**
   * Track chat open/close
   * Matches custom events: ctm-chat-open, ctm-chat-close
   * @param {boolean} isOpen - Whether chat is open
   */
  trackChatToggle(isOpen) {
    this.pushEvent(isOpen ? 'ctm-chat-open' : 'ctm-chat-close', {
      action: isOpen ? 'chat_opened' : 'chat_closed',
      state: isOpen ? 'open' : 'closed'
    });
  }

  /**
   * Track message sent
   * Matches custom events: ctm-chat-sent, ctm-chat-sent-first
   * @param {string} messageType - Type of message (user, ai, agent)
   * @param {Object} metadata - Additional message metadata
   */
  trackMessageSent(messageType, metadata = {}) {
    const isFirstMessage = metadata.isFirstMessage || false;
    this.pushEvent(isFirstMessage ? 'ctm-chat-sent-first' : 'ctm-chat-sent', {
      action: 'message_sent',
      messageType: messageType,
      hasAttachment: metadata.hasAttachment || false,
      messageLength: metadata.messageLength || 0
    });
  }

  /**
   * Track suggestion click
   * Matches custom event: ctm-chat-action with actionType: 'suggestion'
   * GTM event: ctm_chat_action_suggestion
   * @param {string|Object} suggestion - Suggestion that was clicked
   */
  trackSuggestionClick(suggestion) {
    const suggestionText = typeof suggestion === 'string' ? suggestion : suggestion.label;
    this.pushEvent('ctm-chat-action-suggestion', {
      actionType: 'suggestion',
      suggestionText: suggestionText,
      suggestionType: typeof suggestion === 'object' ? suggestion.type : 'text'
    });
  }

  /**
   * Track chat ended
   * Matches custom event: ctm-chat-end
   * @param {string} endedBy - Who ended the chat (user, system, agent)
   */
  trackChatEnd(endedBy = 'user') {
    this.pushEvent('ctm-chat-end', {
      action: 'chat_ended',
      endedBy: endedBy
    });
  }

  /**
   * Track file upload
   * @param {string} fileType - Type of file uploaded
   * @param {number} fileSize - Size of file in bytes
   */
  trackFileUpload(fileType, fileSize) {
    this.pushEvent('ctm-chat-file-upload', {
      action: 'file_uploaded',
      fileType: fileType,
      fileSize: fileSize
    });
  }

  /**
   * Track connection status change
   * @param {string} status - Connection status
   */
  trackConnectionStatus(status) {
    this.pushEvent('ctm-chat-connection', {
      action: 'connection_status_changed',
      status: status
    });
  }

  /**
   * Track form interaction
   * Matches custom event: ctm-chat-action with actionType: 'formDisplay'
   * GTM events: ctm_chat_action_formDisplay, ctm_chat_action_formSubmission
   * @param {string} formAction - Form action (displayed, submitted, closed)
   * @param {Object} formData - Additional form data
   */
  trackFormInteraction(formAction, formData = {}) {
    if (formAction === 'displayed') {
      this.pushEvent('ctm-chat-action-formDisplay', {
        actionType: 'formDisplay',
        formId: formData.formId || null,
        formType: formData.formType || null
      });
    } else if (formAction === 'submitted') {
      this.pushEvent('ctm-chat-action-formSubmission', {
        actionType: 'formSubmission',
        formId: formData.formId || null,
        formType: formData.formType || null
      });
    } else {
      this.pushEvent('ctm-chat-form', {
        action: `form_${formAction}`,
        formId: formData.formId || null,
        formType: formData.formType || null
      });
    }
  }

  /**
   * Track engagement message interaction
   * @param {string} action - Engagement action (displayed, clicked, closed)
   */
  trackEngagement(action) {
    this.pushEvent('ctm-chat-engagement', {
      action: `engagement_${action}`
    });
  }

  /**
   * Track error events
   * @param {string} errorType - Type of error
   * @param {string} errorMessage - Error message
   */
  trackError(errorType, errorMessage) {
    this.pushEvent('ctm-chat-error', {
      action: 'error_occurred',
      errorType: errorType,
      errorMessage: errorMessage
    });
  }

  /**
   * Track session start
   * @param {string} sessionId - Session ID
   * @param {boolean} isReconnect - Whether this is a reconnection
   */
  trackSessionStart(sessionId, isReconnect = false) {
    this.pushEvent('ctm-chat-session-start', {
      action: isReconnect ? 'session_reconnected' : 'session_started',
      sessionId: sessionId
    });
  }

  /**
   * Track agent handoff
   * @param {string} agentType - Type of agent (ai, human)
   */
  trackAgentHandoff(agentType) {
    this.pushEvent('ctm-chat-agent-handoff', {
      action: 'agent_handoff',
      agentType: agentType
    });
  }

  /**
   * Track incoming message (AI or agent response)
   * @param {string} messageType - Type of message (ai, agent)
   * @param {Object} metadata - Additional message metadata
   */
  trackMessageReceived(messageType, metadata = {}) {
    this.pushEvent('ctm-chat-received', {
      action: 'message_received',
      messageType: messageType,
      messageLength: metadata.messageLength || 0
    });
  }

  /**
   * Track generic action with appended type
   * GTM event: ctm_chat_action_[actionType]
   * @param {string} actionType - Type of action
   * @param {Object} data - Action data
   */
  trackAction(actionType, data = {}) {
    // Convert actionType to a GTM-friendly format (camelCase or lowercase)
    const formattedType = actionType.charAt(0).toLowerCase() + actionType.slice(1);
    this.pushEvent(`ctm-chat-action-${formattedType}`, {
      actionType: actionType,
      ...data
    });
  }
}
